import { useModalState } from "@clipboard-health/ui-components";
import { useAppStateContext } from "@src/appV2/App/useAppState";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import type { Worker } from "@src/appV2/Worker/api/types";
import { differenceInSeconds } from "date-fns";
import { useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";

import { type Step } from "./types";

interface UseResumeFromAbandonedStepProps {
  steps: Step[];
  worker: Worker | undefined;
}

function findLastSavedStep(steps: Step[], worker: Worker | undefined): Step | undefined {
  return steps.find((step) => step.stage === worker?.onboardingFlags?.currentSignupStage);
}

const thresholdTimeSinceLastAppActiveInSeconds = 60;

export function useResumeFromAbandonedStep(props: UseResumeFromAbandonedStepProps) {
  const { steps, worker } = props;
  const history = useHistory();

  const {
    state: { isAppActive },
  } = useAppStateContext();
  const lastBecameInactiveAtRef = useRef<Date | undefined>();

  const lastSavedStep = findLastSavedStep(steps, worker);

  // We use this flag to ensure we check and try to resume only once.
  const checkAndTryToResume = useRef(true);
  const lastSavedStage = lastSavedStep?.stage;
  const lastSavedRoute = lastSavedStep?.route;
  const resumeFromAbandonedStepModal = useModalState();

  const lastStepRoute = steps.at(-1)?.route;
  const firstStepRoute = steps[0]?.route;
  const isOnFinalStep = history.location.pathname === lastStepRoute;
  const isOnFirstStep = history.location.pathname === firstStepRoute;

  useEffect(() => {
    // Record when app goes inactive
    if (!isAppActive) {
      lastBecameInactiveAtRef.current = new Date();
      return;
    }

    // On becoming active, compute background duration
    const inactiveAt = lastBecameInactiveAtRef.current;
    if (
      inactiveAt &&
      differenceInSeconds(new Date(), inactiveAt) >= thresholdTimeSinceLastAppActiveInSeconds &&
      !isOnFinalStep &&
      !isOnFirstStep
    ) {
      resumeFromAbandonedStepModal.openModal();
    }

    // Reset the reference to undefined when the app becomes active again
    lastBecameInactiveAtRef.current = undefined;
  }, [isAppActive, isOnFinalStep, isOnFirstStep, resumeFromAbandonedStepModal]);

  useEffect(() => {
    if (
      lastSavedRoute &&
      lastSavedRoute !== history.location.pathname &&
      checkAndTryToResume.current
    ) {
      logEvent(APP_V2_USER_EVENTS.ONBOARDING_RESUMED_FROM_LAST_STEP, {
        workerId: worker?.userId,
        phone: worker?.phone,
        stage: lastSavedStage,
      });

      history.replace(lastSavedRoute);

      if (lastSavedRoute !== lastStepRoute && lastSavedRoute !== firstStepRoute) {
        resumeFromAbandonedStepModal.openModal();
      }
    }

    checkAndTryToResume.current = false;
  }, [
    firstStepRoute,
    history,
    lastSavedRoute,
    lastSavedStage,
    lastStepRoute,
    resumeFromAbandonedStepModal,
    worker?.phone,
    worker?.userId,
  ]);

  return {
    modalState: resumeFromAbandonedStepModal,
  };
}
